import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase'; // 👈 Corregido a ./firebase porque está dentro de src/
import { enviarCorreoRecibo } from './emailService';
import { enviarReciboWhatsAppDirecto } from './whatsappService';

export default function AdminHistorico({
  anioHistorico,
  setAnioHistorico,
  anioActivoBusqueda,
  setAnioActivoBusqueda,
  busquedaHistorico,
  setBusquedaHistorico,
  historicoFiltrado,
  formatearMonto,
  db,
  coleccionHistorico,
  edificioActual,
  propietarios = [], // 👈 Recibimos el directorio actual del edificio como prop
  obtenerRegistros
}) {
  // Estado local para el filtro de mes
  const [mesSeleccionado, setMesSeleccionado] = useState('Todos');
  const [enviandoCorreoId, setEnviandoCorreoId] = useState(null);
  const [enviandoWhatsAppId, setEnviandoWhatsAppId] = useState(null);

  // Estados para el Modal de Edición de Registro Histórico
  const [modalEdicionAbierto, setModalEdicionAbierto] = useState(false);
  const [itemAEditar, setItemAEditar] = useState(null);
  const [nuevoCuota, setNuevoCuota] = useState('');
  const [nuevaFechaEmision, setNuevaFechaEmision] = useState('');
  const [nuevaFechaVencimiento, setNuevaFechaVencimiento] = useState('');
  const [nuevoArchivoPdf, setNuevoArchivoPdf] = useState(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  // Lista de meses disponibles para el filtro
  const meses = ['Todos', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  // Filtrar adicionalmente los datos por el mes seleccionado
  const historicoPorMes = historicoFiltrado.filter(item => {
    if (mesSeleccionado === 'Todos') return true;
    return String(item.mes || '').trim().toLowerCase() === mesSeleccionado.toLowerCase();
  });

  // Función para abrir el modal de edición con los datos actuales
  const abrirModalEdicion = (item) => {
    setItemAEditar(item);
    setNuevoCuota(item.cuota || '');
    setNuevaFechaEmision(item.fechaEmision || '');
    setNuevaFechaVencimiento(item.fechaVencimiento || '');
    setNuevoArchivoPdf(null);
    setModalEdicionAbierto(true);
  };

  // Función para guardar los cambios editados en Firestore y Storage
  const guardarEdicionHistorico = async (e) => {
    e.preventDefault();
    if (!itemAEditar) return;

    try {
      setGuardandoEdicion(true);
      let pdfUrlFinal = itemAEditar.pdfUrl || '';

      // Si el administrador seleccionó un nuevo PDF, lo subimos a Firebase Storage
      if (nuevoArchivoPdf) {
        const edifKey = edificioActual?.id || coleccionHistorico.replace('historico_', '');
        const storageRef = ref(storage, `recibos_${edifKey}/dpto_${itemAEditar.departamento}_${Date.now()}_${nuevoArchivoPdf.name}`);
        await uploadBytes(storageRef, nuevoArchivoPdf);
        pdfUrlFinal = await getDownloadURL(storageRef);
      }

      const refDoc = doc(db, coleccionHistorico, itemAEditar.id);
      await updateDoc(refDoc, {
        cuota: parseFloat(nuevoCuota) || 0,
        fechaEmision: nuevaFechaEmision,
        fechaVencimiento: nuevaFechaVencimiento,
        pdfUrl: pdfUrlFinal,
        pdfUrls: [{ url: pdfUrlFinal }] // 👈 Actualizamos también el array para que la tabla y el portal lo detecten
      });

      await obtenerRegistros();
      setModalEdicionAbierto(false);
      setItemAEditar(null);
      alert("✅ ¡Registro histórico y PDF actualizados con éxito!");
    } catch (error) {
      console.error("Error al actualizar registro histórico:", error);
      alert("❌ Hubo un error al guardar los cambios.");
    } finally {
      setGuardandoEdicion(false);
    }
  };

  // Función para reenviar el recibo individualmente por correo (con respaldo en el directorio)
  const manejarReenvioCorreo = async (item) => {
    const propDirectorio = propietarios.find(p => String(p.departamento).trim() === String(item.departamento).trim());
    const correoFinal = item.correo || propDirectorio?.correo;

    if (!correoFinal || !correoFinal.includes('@')) {
      alert(`⚠️ El Dpto. ${item.departamento} no tiene un correo electrónico válido registrado ni en el histórico ni en el directorio.`);
      return;
    }

    if (window.confirm(`¿Deseas reenviar el recibo por correo al Dpto. ${item.departamento} (${item.nombre}) a ${correoFinal}?`)) {
      try {
        setEnviandoCorreoId(item.id);
        const montoCuota = formatearMonto(item.cuota || 0);
        
        const edifId = edificioActual?.id || (coleccionHistorico ? coleccionHistorico.replace('historico_', '') : 'EDIF');
        const numeroReciboRef = `REC-${edifId.toUpperCase()}-${item.departamento}`;

        await enviarCorreoRecibo(
          correoFinal,
          item.nombre,
          numeroReciboRef,
          item.mes || mesSeleccionado,
          item.anio || anioHistorico,
          montoCuota,
          item.fechaEmision || '-',
          item.fechaVencimiento || '-'
        );

        alert(`✅ ¡Recibo reenviado con éxito a ${correoFinal}!`);
      } catch (error) {
        console.error("Error al reenviar correo:", error);
        alert("❌ Hubo un error al intentar enviar el correo.");
      } finally {
        setEnviandoCorreoId(null);
      }
    }
  };

  // Función para reenviar el recibo individualmente por WhatsApp (con respaldo en el directorio)
  const manejarReenvioWhatsApp = async (item) => {
    const propDirectorio = propietarios.find(p => String(p.departamento).trim() === String(item.departamento).trim());
    const telefonoRaw = item.telefono || item.celular || propDirectorio?.telefono || propDirectorio?.celular;

    if (!telefonoRaw || telefonoRaw === '-' || telefonoRaw.trim() === '') {
      alert(`⚠️ El Dpto. ${item.departamento} no tiene un número de teléfono/celular registrado ni en el histórico ni en el directorio.`);
      return;
    }

    const cuotaTexto = formatearMonto(item.cuota || 0);
    const edifId = edificioActual?.id || (coleccionHistorico ? coleccionHistorico.replace('historico_', '') : 'Edificio MMD&L');
    const nombreEdificio = edificioActual?.nombre || `Edificio ${edifId.toUpperCase()}`;
    const pdfParaEnviar = item.pdfUrl || (item.pdfUrls?.[0]?.url || '');

    if (window.confirm(`¿Enviar recibo por WhatsApp a ${item.nombre} (${telefonoRaw})?`)) {
      try {
        setEnviandoWhatsAppId(item.id);
        const resultado = await enviarReciboWhatsAppDirecto(
          telefonoRaw,
          item.nombre,
          item.departamento,
          item.mes || mesSeleccionado,
          item.anio || anioHistorico,
          cuotaTexto,
          item.fechaEmision || '-',
          item.fechaVencimiento || '-',
          pdfParaEnviar,
          nombreEdificio
        );

        if (resultado.success) {
          alert(`✅ ¡Aviso de recibo por WhatsApp preparado con éxito para ${item.nombre}!`);
        } else {
          throw new Error(resultado.error);
        }
      } catch (error) {
        console.error("Error al procesar WhatsApp de recibo:", error);
        alert(`❌ No se pudo procesar el WhatsApp: ${error.message}`);
      } finally {
        setEnviandoWhatsAppId(null);
      }
    }
  };

  // Función inteligente para alternar (aprobar o deshacer) el estado de pago
  const toggleAprobarPagoHistorico = async (item) => {
    const esAprobado = item.estadoPago === "Pago verificado y aprobado ✅";
    const nuevoEstado = esAprobado 
      ? (item.voucherUrl ? "En revisión por administración" : "Pendiente de validación")
      : "Pago verificado y aprobado ✅";

    const mensaje = esAprobado 
      ? `⚠️ ¿Deshacer la aprobación del pago del Dpto. ${item.departamento} (${item.nombre})? El estado regresará a su color normal.`
      : `¿Estás seguro de aprobar el pago del Dpto. ${item.departamento} (${item.nombre})?`;

    if (window.confirm(mensaje)) {
      try {
        const refDoc = doc(db, coleccionHistorico, item.id);
        await updateDoc(refDoc, { estadoPago: nuevoEstado });
        await obtenerRegistros();
        if (esAprobado) {
          alert(`⚠️ ¡Aprobación deshecha! El estado del Dpto. ${item.departamento} volvió a la normalidad.`);
        } else {
          alert(`✅ ¡Pago del Dpto. ${item.departamento} aprobado con éxito!`);
        }
      } catch (error) {
        console.error("Error al actualizar estado de pago:", error);
        alert("❌ Ocurrió un error al intentar actualizar el pago.");
      }
    }
  };

  // Función para borrar masivamente los registros del filtro actual
  const borrarHistorialPorMes = async () => {
    if (historicoPorMes.length === 0) {
      alert("⚠️ No hay registros en el filtro actual para borrar.");
      return;
    }

    const mensaje = mesSeleccionado === 'Todos'
      ? `⚠️ ¿Estás seguro de eliminar TODOS los ${historicoPorMes.length} registros históricos del año ${anioHistorico}? Esta acción no se puede deshacer.`
      : `⚠️ ¿Estás seguro de eliminar masivamente los ${historicoPorMes.length} registros del mes de ${mesSeleccionado} (${anioHistorico})? Esta acción no se puede deshacer.`;

    if (window.confirm(mensaje)) {
      try {
        const batch = writeBatch(db);
        historicoPorMes.forEach(item => {
          const refDoc = doc(db, coleccionHistorico, item.id);
          batch.delete(refDoc);
        });

        await batch.commit();
        await obtenerRegistros();
        alert("🗑️ ¡Registros eliminados masivamente con éxito!");
      } catch (error) {
        console.error("Error al borrar registros en lote:", error);
        alert("Hubo un error al eliminar los registros.");
      }
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0, color: '#1e293b' }}>Archivo Histórico General (Por Año y Mes)</h2>
      <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>Consulta el histórico anual y mensual de todos los departamentos y audita los vouchers de pago.</p>

      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px 20px', marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <label style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>📅 Año:</label>
          <select value={anioHistorico} onChange={e => setAnioHistorico(e.target.value)} style={{ marginLeft: '6px', padding: '6px', fontWeight: 'bold' }}>
            {['2024','2025','2026','2027','2028','2029','2030'].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>🗓️ Mes:</label>
          <select value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)} style={{ marginLeft: '6px', padding: '6px', fontWeight: 'bold' }}>
            {meses.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <button onClick={() => {
          setAnioActivoBusqueda(anioHistorico);
          alert(`🔍 Mostrando registros históricos del año ${anioHistorico}${mesSeleccionado !== 'Todos' ? ` y mes de ${mesSeleccionado}` : ''}`);
        }} style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
          🔍 Aplicar Filtros
        </button>

        <button onClick={borrarHistorialPorMes} style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
          🗑️ Borrar Filtro Actual ({historicoPorMes.length})
        </button>
          
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
          <input 
            type="text" 
            placeholder="Buscar Dpto o Propietario..." 
            value={busquedaHistorico} 
            onChange={e => setBusquedaHistorico(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '210px' }}
          />
          {busquedaHistorico && (
            <button onClick={() => setBusquedaHistorico('')} style={{ backgroundColor: '#94a3b8', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>
              ✖️
            </button>
          )}
        </div>
      </div>

      <table className="tabla-propietarios">
        <thead>
          <tr>
            <th>Dpto.</th>
            <th>Propietario</th>
            <th>Mes / Periodo</th>
            <th>Cuota</th>
            <th>Recibo PDF</th>
            <th>Voucher del Propietario</th>
            <th>Estado / Validar Pago</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {historicoPorMes.length === 0 ? (
            <tr><td colSpan="8" style={{ textAlign: 'center', padding: '30px', color: '#666' }}>No hay registros históricos para los filtros seleccionados.</td></tr>
          ) : (
            historicoPorMes.map(item => {
              const esAprobado = item.estadoPago === "Pago verificado y aprobado ✅";

              return (
                <tr key={item.id}>
                  <td><strong>{item.departamento}</strong></td>
                  <td>{item.nombre}</td>
                  <td><strong>{item.mes || '-'}</strong></td>
                  <td>{formatearMonto(item.cuota)}</td>
                  <td>
                    {item.pdfUrls && item.pdfUrls.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {item.pdfUrls.map((pdf, idx) => {
                          const urlPdf = pdf.url || pdf;
                          return (
                            <a key={idx} href={urlPdf} target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', fontWeight: 'bold', textDecoration: 'underline', fontSize: '0.8rem' }}>
                              📄 Ver PDF {idx + 1} ↗
                            </a>
                          );
                        })}
                      </div>
                    ) : item.pdfUrl ? (
                      <a href={item.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', fontWeight: 'bold', textDecoration: 'underline' }}>📄 Ver PDF ↗</a>
                    ) : (
                      <span style={{ color: '#64748b', fontStyle: 'italic' }}>Sin PDF</span>
                    )}
                  </td>
                  <td>
                    {item.voucherUrl ? (
                      <a href={item.voucherUrl} target="_blank" rel="noopener noreferrer" style={{ backgroundColor: '#0284c7', color: 'white', padding: '5px 10px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', textDecoration: 'none', display: 'inline-block' }}>
                        👁️ Ver Voucher ↗
                      </a>
                    ) : (
                      <span style={{ color: '#d9534f', fontStyle: 'italic', fontWeight: 'bold' }}>Aún sin voucher</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <span style={{ 
                        backgroundColor: esAprobado ? '#f3e8ff' : (item.voucherUrl ? '#dcfce7' : '#fef3c7'), 
                        color: esAprobado ? '#6b21a8' : (item.voucherUrl ? '#166534' : '#d97706'), 
                        padding: '3px 6px', 
                        borderRadius: '4px', 
                        fontWeight: 'bold', 
                        fontSize: '0.75rem', 
                        textAlign: 'center',
                        border: esAprobado ? '1px solid #d8b4fe' : 'none'
                      }}>
                        {item.estadoPago || 'Pendiente'}
                      </span>
                      
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => toggleAprobarPagoHistorico(item)} 
                          style={{ 
                            backgroundColor: esAprobado ? '#7c3aed' : '#16a34a', 
                            color: 'white', 
                            border: 'none', 
                            padding: '5px 6px', 
                            borderRadius: '4px', 
                            fontSize: '0.7rem', 
                            fontWeight: 'bold', 
                            cursor: 'pointer', 
                            width: '100%' 
                          }}
                          title={esAprobado ? "Deshacer aprobación" : "Aprobar pago"}
                        >
                          {esAprobado ? "Deshacer ↩️" : "Aprobar ✔️"}
                        </button>
                      </div>

                      <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                        <button 
                          onClick={() => manejarReenvioCorreo(item)}
                          disabled={enviandoCorreoId === item.id}
                          style={{ backgroundColor: '#0d9488', color: 'white', border: 'none', padding: '5px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}
                          title="Reenviar recibo por correo electrónico"
                        >
                          {enviandoCorreoId === item.id ? '⏳...' : '📧 Correo'}
                        </button>

                        <button 
                          onClick={() => manejarReenvioWhatsApp(item)}
                          disabled={enviandoWhatsAppId === item.id}
                          style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '5px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}
                          title="Enviar recibo por WhatsApp"
                        >
                          {enviandoWhatsAppId === item.id ? '⏳...' : '📱 Wsp'}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <button 
                        onClick={() => abrirModalEdicion(item)} 
                        style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer' }}
                        title="Editar cuota, fechas o PDF"
                      >
                        ✏️ Editar
                      </button>

                      <button 
                        onClick={async () => {
                          if (window.confirm(`⚠️ ¿Deseas eliminar este registro histórico del Dpto. ${item.departamento}?`)) {
                            await deleteDoc(doc(db, coleccionHistorico, item.id));
                            await obtenerRegistros();
                            alert("¡Registro eliminado con éxito!");
                          }
                        }} 
                        style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer' }}
                      >
                        🗑️ Borrar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* MODAL DE EDICIÓN DE REGISTRO HISTÓRICO */}
      {modalEdicionAbierto && itemAEditar && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '15px' }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', width: '100%', maxWidth: '450px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h3 style={{ marginTop: 0, color: '#1e293b', marginBottom: '15px' }}>
              ✏️ Editar Registro — Dpto. {itemAEditar.departamento} ({itemAEditar.nombre})
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
              Periodo: <strong>{itemAEditar.mes} {itemAEditar.anio}</strong>
            </p>

            <form onSubmit={guardarEdicionHistorico} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '4px', color: '#334155' }}>Monto de Cuota (S/):</label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={nuevoCuota} 
                  onChange={e => setNuevoCuota(e.target.value)} 
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '4px', color: '#334155' }}>Fecha de Emisión:</label>
                <input 
                  type="text" 
                  value={nuevaFechaEmision} 
                  onChange={e => setNuevaFechaEmision(e.target.value)} 
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  placeholder="Ej. 01/08/2026"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '4px', color: '#334155' }}>Fecha de Vencimiento:</label>
                <input 
                  type="text" 
                  value={nuevaFechaVencimiento} 
                  onChange={e => setNuevaFechaVencimiento(e.target.value)} 
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  placeholder="Ej. 15/08/2026"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '4px', color: '#334155' }}>
                  Actualizar Recibo PDF (Opcional):
                </label>
                {itemAEditar.pdfUrl && (
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.78rem' }}>
                    <a href={itemAEditar.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7' }}>Ver PDF actual ↗</a>
                  </p>
                )}
                <input 
                  type="file" 
                  accept=".pdf" 
                  onChange={e => setNuevoArchivoPdf(e.target.files[0])} 
                  style={{ width: '100%', fontSize: '0.85rem' }}
                />
                <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '3px' }}>Si no seleccionas ningún archivo, se mantendrá el PDF actual.</span>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button 
                  type="submit" 
                  disabled={guardandoEdicion}
                  style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}
                >
                  {guardandoEdicion ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setModalEdicionAbierto(false)}
                  style={{ backgroundColor: '#cbd5e1', color: '#334155', border: 'none', padding: '10px 15px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}